import { CYBER_SAMPLE, REFERENCE_SCENARIO } from "@/lib/pathwayData";

const pad = (n) => String(n).padStart(2, "0");

export function makeReference(prefix = "ERLP") {
  const now = new Date();
  const stamp =
    now.getUTCFullYear() +
    pad(now.getUTCMonth() + 1) +
    pad(now.getUTCDate()) +
    "-" +
    pad(now.getUTCHours()) +
    pad(now.getUTCMinutes()) +
    pad(now.getUTCSeconds());
  const tail = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `${prefix}-${stamp}-${tail}`;
}

// Synthetic, deterministic-looking evidence hash. Never a real cryptographic
// hash of real data.
function syntheticHash(seed = "") {
  const base = (seed + Math.random().toString(36) + Date.now().toString(36))
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase()
    .padEnd(40, "0");
  return "0x" + base.slice(0, 40);
}

// Public-safe illustrative-preview receipt. This browser-generated artefact
// is NEVER a KATLAS-signed receipt regardless of runtime status. The sponsor
// shell does not call POST /v1/receipts/sign; runtime availability proves
// reachability + authentication only. Individual receipt signing is a
// separate operation that only exists in the Journey 01 workspace.
function baseReceipt(overrides = {}, runtime = null) {
  const ref = overrides.reference || makeReference();
  const ts = new Date().toISOString();
  const runtime_status_observed = runtime?.status || "unknown";
  const verification_state = "ILLUSTRATIVE PREVIEW — NOT A KATLAS-SIGNED RECEIPT";
  return {
    schema: "lab-pathway.public-safe-receipt/v1",
    product: "Emerging Risk Lab Pathway",
    mode: "insurance",
    receipt_category: "illustrative_preview",
    runtime_status_observed,
    runtime_node_id: runtime?.node_id || null,
    runtime_version: runtime?.version || null,
    illustrative_only: true,
    notice:
      "Illustrative pathway artefact. Client-generated, browser-side only. No live KATLAS signing has occurred. Not a legal attestation, not a regulated decision, not an actual sponsor submission. Named organisations (where shown) are placeholders only and do not imply endorsement or participation.",
    receipt_id: ref,
    reference: ref,
    generated_at: ts,
    timestamp: ts,
    policy_version: "lab-pathway.insurance/2026.06",
    sponsor_theme: "Cyber referral governance",
    risk_scenario: CYBER_SAMPLE.title,
    participants: [
      { role: "broker", attribution: "illustrative" },
      { role: "client", attribution: "illustrative" },
      { role: "cyber assessor", attribution: "illustrative" },
      { role: "authorised underwriter", attribution: "illustrative" },
      { role: "MGA operations", attribution: "illustrative" },
    ],
    actor_roles: [
      "broker",
      "client",
      "cyber assessor",
      "authorised underwriter",
      "MGA operations",
    ],
    authority_chain: [
      { actor: "broker", action: "submission", at: ts, note: "synthetic" },
      {
        actor: "AI assistant",
        action: "summarise gaps · suggest next evidence",
        at: ts,
        note: "bounded · cannot decide",
      },
      {
        actor: "authorised underwriter",
        action: "refer for governed review",
        at: ts,
        note: "illustrative — no live decision",
      },
    ],
    timeline: [
      { time: "09:12", event: "Broker submission" },
      { time: "09:15", event: "AI recommendation" },
      { time: "09:17", event: "Authority verified" },
      { time: "09:18", event: "Exception raised" },
      { time: "09:22", event: "Human review" },
      { time: "09:24", event: "Receipt signed" },
      { time: "09:24", event: "Decision bound" },
    ],
    confidence: {
      authority: "98%",
      evidence: "94%",
      policy_alignment: "100%",
      decision_confidence: "96%",
      human_review: "Required",
    },
    explain: {
      decision: "Referral",
      reason: "Conflicting delegated authority",
      evidence_used: [
        "Broker submission",
        "Email 1 — authority granted",
        "Email 2 — contradicting instruction",
        "Policy 2.4 — delegated authority schedule",
      ],
      authority_chain_summary: {
        verified: true,
        escalated: true,
        reason: "Conflicting delegated authority",
      },
      receipt_status: "Generated",
    },
    evidence_requested: [
      "client cyber posture summary",
      "incident-response readiness statement",
      "assessor technical findings",
    ],
    evidence_withheld: [
      "raw client telemetry",
      "named personnel detail",
      "internal pricing models",
    ],
    evidence_hash: syntheticHash(ref),
    ai_use_boundary:
      "AI may summarise evidence and suggest next requests. AI must not bind, decline, settle or make regulated decisions.",
    decision: "refer",
    authorised_decision:
      "Refer to authorised underwriter for governed review and resolution.",
    suggested_next_step:
      "Request assessor's technical findings v2 and client's incident-response readiness statement.",
    verification_state,
    ...overrides,
  };
}

export function cyberSampleReceipt(runtime = null) {
  return baseReceipt({}, runtime);
}

export function conflictingInstructionsReceipt(runtime = null) {
  const ref = makeReference();
  const ts = new Date().toISOString();
  return baseReceipt(
    {
      reference: ref,
      receipt_id: ref,
      generated_at: ts,
      timestamp: ts,
      sponsor_theme: "Operational efficiency · conflicting instructions",
    risk_scenario: REFERENCE_SCENARIO.title,
    participants: [
      { role: "senior underwriter (instructing)", attribution: "illustrative" },
      { role: "senior underwriter (contradicting)", attribution: "illustrative" },
      { role: "MGA operations", attribution: "illustrative" },
      { role: "broker", attribution: "illustrative" },
      { role: "reinsurance attachment", attribution: "illustrative" },
    ],
    actor_roles: [
      "senior underwriter (instructing)",
      "senior underwriter (contradicting)",
      "MGA operations",
      "broker",
      "reinsurance attachment",
    ],
    authority_chain: [
      {
        actor: "senior underwriter (instructing)",
        action: "grant binding authority via email 1",
        at: ts,
        note: "synthetic",
      },
      {
        actor: "MGA operations",
        action: "begin downstream commitments",
        at: ts,
        note: "queued reinsurance attachment",
      },
      {
        actor: "senior underwriter (contradicting)",
        action: "withdraw / qualify authority via email 2",
        at: ts,
        note: "governance event",
      },
      {
        actor: "AI assistant",
        action: "summarise divergence · flag evidence gaps",
        at: ts,
        note: "bounded · cannot decide",
      },
      {
        actor: "authorised underwriter",
        action: "issue resolving instruction",
        at: ts,
        note: "illustrative — no live decision",
      },
    ],
    evidence_requested: [
      "instruction email 1 (authority granted)",
      "instruction email 2 (contradicting)",
      "downstream commitment log",
    ],
    evidence_withheld: [
      "internal mailbox metadata",
      "named personnel detail",
      "policyholder-identifying data",
    ],
    ai_use_boundary:
      "AI summarised the divergence between instructions and identified evidence gaps. AI did not resolve, decide or bind.",
    decision: "resolved · governed acceptance",
    authorised_decision:
      "Illustrative only — an authorised underwriter would resolve the conflict and reconcile downstream commitments under governed acceptance.",
    suggested_next_step:
      "Capture resolution as a governance event; route reversal/confirmation notices to broker and reinsurance attachment.",
    timeline: [
      { time: "09:12", event: "Broker submission" },
      { time: "09:15", event: "AI recommendation" },
      { time: "09:17", event: "Authority verified — email 1" },
      { time: "09:19", event: "Downstream commitments queued" },
      { time: "09:21", event: "Email 2 received — contradicting" },
      { time: "09:21", event: "Exception raised — governance node" },
      { time: "09:26", event: "Human review — authorised underwriter" },
      { time: "09:28", event: "Receipt signed" },
      { time: "09:28", event: "Decision bound" },
    ],
    explain: {
      decision: "Refer · governed acceptance",
      reason: "Conflicting delegated authority across two instructions",
      evidence_used: [
        "Broker submission",
        "Email 1 — authority granted",
        "Email 2 — contradicting instruction",
        "Downstream commitment log",
        "Policy 2.4 — delegated authority schedule",
      ],
      authority_chain_summary: {
        verified: true,
        escalated: true,
        reason: "Conflicting delegated authority",
      },
      receipt_status: "Generated",
    },
    },
    runtime,
  );
}

export function intakeReceipt(form, reference, runtime = null) {
  const ts = new Date().toISOString();
  return baseReceipt({
    schema: "lab-pathway.sponsor-intake-receipt/v1",
    reference,
    receipt_id: reference,
    generated_at: ts,
    timestamp: ts,
    sponsor_theme: form.insuranceLine || "Sponsor intake",
    risk_scenario: "Sponsor intake submission",
    participants: [
      { role: "sponsor (intake)", attribution: "illustrative" },
      { role: "Lab Pathway intake desk", attribution: "illustrative" },
    ],
    actor_roles: ["sponsor (intake)", "Lab Pathway intake desk"],
    authority_chain: [
      {
        actor: "sponsor",
        action: "submit intake",
        at: ts,
        note: "synthetic record",
      },
      {
        actor: "Lab Pathway intake desk",
        action: "acknowledge · route for governed review",
        at: ts,
        note: "no automated decision",
      },
    ],
    evidence_requested: [
      "sponsor organisation",
      "insurance line of interest",
      "target market audience",
      "desired thought-leadership output",
      "indicative budget band",
    ],
    evidence_withheld: [
      "named contact email is captured but not displayed publicly",
      "internal scoring is not captured in this lab",
    ],
    ai_use_boundary:
      "No AI processing has been applied to this intake. The submission is a synthetic record only.",
    decision: "acknowledged",
    authorised_decision:
      "Illustrative acknowledgement only — not a binding intake. Routed for governed review.",
    suggested_next_step:
      "Sponsor desk would (in a live engagement) respond with a sponsor pathway proposal and indicative budget band.",
    intake: {
      organisation: form.organisation || null,
      insurance_line: form.insuranceLine || null,
      target_audiences: form.audiences || [],
      desired_outputs: form.outputs || [],
      indicative_budget_band: form.budget || null,
    },
  }, runtime);
}

export function downloadJson(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// Representative API surface — illustrative only.
export function buildApiPreview(receipt) {
  const id = receipt?.receipt_id || receipt?.reference || "ERLP-…";
  return {
    request: `GET https://api.katlas.example/v1/receipts/${id}
Accept: application/json
Authorization: Bearer ***   # held server-side only`,
    response: JSON.stringify(
      {
        receipt_id: id,
        schema: receipt?.schema,
        policy_version: receipt?.policy_version,
        decision: receipt?.decision,
        verification_state: receipt?.verification_state,
        authority_chain: receipt?.authority_chain,
        evidence_hash: receipt?.evidence_hash,
        participants: receipt?.participants,
        timestamp: receipt?.timestamp,
      },
      null,
      2
    ),
    note:
      "Illustrative API surface only. No live endpoint. No production keys. No data transit. This panel shows the shape of a runtime call in a live KATLAS deployment.",
  };
}
